import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useAuthContext } from '@/domains/auth/authContext';

const featureFlagsResponseSchema = z.object({
  featureFlags: z.record(z.string(), z.boolean()),
});
export type FeatureFlagsType = z.infer<
  typeof featureFlagsResponseSchema
>['featureFlags'];

const FEATURE_FLAGS_API_URL = process.env.NEXT_PUBLIC_FEATURE_FLAGS_API_URL;
const LOCAL_OVERRIDE = process.env.NEXT_PUBLIC_FEATURE_FLAGS_LOCAL_OVERRIDE;
const SHOULD_USE_LOCAL_OVERRIDE =
  process.env.NODE_ENV === 'development' && Boolean(LOCAL_OVERRIDE);

if (!FEATURE_FLAGS_API_URL && !SHOULD_USE_LOCAL_OVERRIDE) {
  console.warn(
    'FEATURE_FLAGS_API_URL not set - feature flags will be disabled',
  );
}

export const useFeatureFlagsQuery = () => {
  const { getIdToken, currentUser } = useAuthContext();

  return useQuery<FeatureFlagsType>({
    queryKey: ['featureFlags', currentUser?.uid],
    enabled:
      !!currentUser && (!!FEATURE_FLAGS_API_URL || SHOULD_USE_LOCAL_OVERRIDE),
    retry: false,
    queryFn: async () => {
      if (SHOULD_USE_LOCAL_OVERRIDE && LOCAL_OVERRIDE) {
        return LOCAL_OVERRIDE.split(',')
          .map((flag) => flag.trim())
          .filter(Boolean)
          .reduce<FeatureFlagsType>((acc, flag) => {
            acc[flag] = true;
            return acc;
          }, {});
      }

      const accessToken = await getIdToken();
      if (!accessToken) {
        console.warn('Unable to get authentication token for feature flags');
        return {};
      }
      const response = await fetch(`${FEATURE_FLAGS_API_URL}/flags`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        console.error('Failed to fetch feature flags');
        return {};
      }
      const data = await response.json();
      const parsed = featureFlagsResponseSchema.parse(data);
      return parsed.featureFlags;
    },
    staleTime: 1000 * 60 * 5,
  });
};
